// @ts-nocheck
import * as React from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';
import Loader from 'components/uielements/Loader';
import { partnerProgramsActions } from 'ducks/partnerPrograms/actions';
import type { SorterOption } from 'commons/types';
import MetaTags from 'components/MetaTags';
import ProgramGroup from './components/ProgramGroup';
import RequestModal from './components/RequestModal';
import RequestList from './components/RequestList';
import Sorter from './components/Sorter';
import PartnerProgramsWrapper from './PartnerPrograms.style';
import { DESCRIPTION, HEADER } from './constants';
import type {
  PartnerProgramsProps,
  ProgramGroupListProps,
  SorterSectionProps,
} from './PartnerPrograms.types';

const getClearedSorterOptions = (sorterOptions: SorterOption[]): SorterOption[] =>
  sorterOptions.map((o) => ({ ...o, value: false }));

const ProgramGroupList = ({
  isLoading,
  programGroups,
  onSelectProgram,
  onToggleModal,
}: ProgramGroupListProps): React.ReactElement<any> | React.ReactElement<any>[] =>
  isLoading ? (
    <Loader />
  ) : (
    programGroups.map((pg) => (
      <ProgramGroup
        key={pg.name}
        programGroup={pg}
        onSelectProgram={onSelectProgram}
        onToggleModal={onToggleModal}
      />
    ))
  );

const SorterSection = ({
  isLoading,
  onChange,
  onClear,
  onSave,
  sorterOptions,
}: SorterSectionProps): React.ReactElement<any> => (
  <div className='sorter-section'>
    <span className='sorter-section__sort-by'>Sort by:</span>
    <Sorter
      isLoading={isLoading}
      name='Program Category'
      onChange={onChange}
      onClear={onClear}
      onSave={onSave}
      options={sorterOptions}
    />
  </div>
);

const PartnerPrograms = ({
  isLoading,
  isModalOpen,
  onGetProgramGroups,
  onRequestInfo,
  onResetList,
  onSelectProgram,
  onToggleModal,
  onUpdateSorterOptions,
  programGroups,
  selectedPrograms,
  sorterOptions,
  sorterValue,
}: PartnerProgramsProps) => {
  React.useEffect(() => {
    onGetProgramGroups();
  }, [onGetProgramGroups]);
  const handleOnSorterChange = React.useCallback(
    (selectedOptionKey: string, mode?: string): void => {
      const clearedSorterOptions = getClearedSorterOptions(sorterOptions);
      const newOption = clearedSorterOptions.find((o) => o.key === selectedOptionKey) || {};

      if (Object.keys(newOption).length) {
        newOption.value = true;
        onUpdateSorterOptions(clearedSorterOptions);

        if (mode && mode === 'autoSet') {
          onGetProgramGroups(clearedSorterOptions);
        }
      }
    }, // Let's not add unnecessary dependencies
    // eslint-disable-next-line
    [onUpdateSorterOptions, sorterOptions],
  );
  React.useEffect(() => {
    const isQuerySortable =
      sorterValue &&
      sorterOptions &&
      sorterOptions.length &&
      sorterOptions.every((option) => !option.value);

    if (isQuerySortable) {
      handleOnSorterChange(sorterValue, 'autoSet');
    } // Let's not add unnecessary dependencies
    // eslint-disable-next-line
  }, [sorterOptions, sorterValue]);

  const handleOnSorterSave = (): void => onGetProgramGroups(sorterOptions);

  const handleOnSorterClear = (): void =>
    onGetProgramGroups(getClearedSorterOptions(sorterOptions));

  const programGroupListProps = {
    isLoading,
    programGroups,
    onSelectProgram,
    onToggleModal,
  };
  const sorterSectionProps = {
    isLoading,
    onChange: handleOnSorterChange,
    onClear: handleOnSorterClear,
    onSave: handleOnSorterSave,
    sorterOptions,
  };
  return (
    <PartnerProgramsWrapper>
      <MetaTags
        title='Partner Programs'
        ogTitle='Partner Programs'
        ogType='website'
        twitterCard='summary'
        noIndex
      />
      <div className='partner-programs__container'>
        <h1 className='partner-programs__header'>{HEADER}</h1>
        <span className='partner-programs__description'>{DESCRIPTION}</span>
        <SorterSection {...sorterSectionProps} />
        <ProgramGroupList {...programGroupListProps} />
        <RequestList
          selectedPrograms={selectedPrograms.length}
          onResetList={onResetList}
          onToggleModal={onToggleModal}
        />
        <RequestModal
          isOpen={isModalOpen}
          onRequestInfo={onRequestInfo}
          onToggleModal={onToggleModal}
          selectedPrograms={selectedPrograms}
        />
      </div>
    </PartnerProgramsWrapper>
  );
};

const mapStateToProps = (state) => ({
  isLoading: get(state, 'partnerPrograms.isLoading', true),
  isModalOpen: get(state, 'partnerPrograms.requestInfo.isModalOpen', false),
  programGroups: get(state, 'partnerPrograms.programGroups', []),
  selectedPrograms: Object.values(get(state, 'partnerPrograms.selectedPrograms', {})),
  sorterOptions: get(state, 'partnerPrograms.sorterOptions', []),
  sorterValue: decodeURI(get(state, 'router.location.query.sorterValue', '')),
});

const mapDispatchToProps = {
  onGetProgramGroups: (sorterOptions) =>
    partnerProgramsActions.getProgramGroupsRequest(sorterOptions),
  onSelectProgram: (program) => partnerProgramsActions.selectProgram(program),
  onRequestInfo: (selectedPrograms, userInfo) =>
    partnerProgramsActions.requestInfoRequest({
      selectedPrograms,
      userInfo,
    }),
  onResetList: () => partnerProgramsActions.resetProgramList(),
  onToggleModal: () => partnerProgramsActions.toggleRequestInfoModal(),
  onUpdateSorterOptions: (sorterOptions) =>
    partnerProgramsActions.updateSorterOptions(sorterOptions),
};
export default connect(mapStateToProps, mapDispatchToProps)(PartnerPrograms);
